import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { BRAND_LUXE_GOLD, BRAND_SURFACE_CONTAINER, BRAND_SURFACE_CONTAINER_LOW } from './brandDashboardTokens';
import type { ICampaignPlatformConfig, IInfluencerTierConfig } from '../../types/global/ugcCampaignConfig.types';
import { isPlatformComingSoon, isPlatformSelectable } from '../../utils/ugcCampaignConfig';

const CHIP_BG = '#222222';
const CARD_BG = '#141414';
const OBSIDIAN = '#0A0A0A';

const GOLD_GLOW_SM = '0 0 15px rgba(255, 193, 7, 0.4)';
const GOLD_GLOW_LG = '0 0 25px rgba(255, 193, 7, 0.5)';

const sectionLabelSx = {
    fontSize: 11,
    fontFamily: FONT_ENUM.MAIN_BOLD,
    fontWeight: 800,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.2em',
    color: 'rgba(255,255,255,0.45)',
    ml: 0.5,
    mb: 2,
};

const chipTransition = {
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
};

export function UgcTargetingSectionLabel({ children }: { children: React.ReactNode }) {
    return <Typography sx={sectionLabelSx}>{children}</Typography>;
}

export function UgcRewardTypeChips({
    value,
    options,
    onChange,
    disabled = false,
}: {
    value: string;
    options: ReadonlyArray<{ value: string; label: string }>;
    onChange: (value: string) => void;
    disabled?: boolean;
}) {
    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1.5, sm: 2 }, opacity: disabled ? 0.55 : 1 }}>
            {options.map(({ value: optValue, label }) => {
                const active = value === optValue;
                return (
                    <Box
                        key={optValue}
                        component="button"
                        type="button"
                        disabled={disabled}
                        onClick={() => {
                            if (!disabled) onChange(optValue);
                        }}
                        sx={{
                            ...chipTransition,
                            border: '1px solid',
                            borderColor: active ? BRAND_LUXE_GOLD : 'rgba(255,255,255,0.06)',
                            borderRadius: '16px',
                            px: { xs: 3, sm: 4 },
                            py: { xs: 1.5, sm: 2 },
                            bgcolor: active ? BRAND_LUXE_GOLD : CHIP_BG,
                            color: active ? OBSIDIAN : '#fff',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: { xs: 15, sm: 18 },
                            fontWeight: active ? 800 : 700,
                            cursor: disabled ? 'not-allowed' : 'pointer',
                            boxShadow: active ? GOLD_GLOW_LG : 'none',
                            transform: active ? 'scale(1.03)' : 'none',
                            '&:hover': {
                                bgcolor: active ? BRAND_LUXE_GOLD : 'rgba(34,34,34,0.85)',
                                borderColor: active ? BRAND_LUXE_GOLD : 'rgba(255,255,255,0.12)',
                            },
                            '&:focus-visible': {
                                outline: `2px solid ${BRAND_LUXE_GOLD}`,
                                outlineOffset: 2,
                            },
                        }}>
                        {label}
                    </Box>
                );
            })}
        </Box>
    );
}

export function UgcPlatformChips({
    platforms,
    selectedIds,
    onToggle,
    disabled = false,
}: {
    platforms: ICampaignPlatformConfig[];
    selectedIds: string[];
    onToggle: (platformId: string) => void;
    disabled?: boolean;
}) {
    return (
        <Box
            sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: { xs: 1.5, sm: 2 },
                alignItems: 'center',
                opacity: disabled ? 0.55 : 1,
            }}>
            {platforms.map(platform => {
                const locked = isPlatformComingSoon(platform);
                const selectable = isPlatformSelectable(platform);
                const selected = selectable && selectedIds.includes(platform.id);

                if (!selectable && !locked) return null;

                if (locked) {
                    return (
                        <Tooltip key={platform.id} title="Coming soon" placement="top">
                            <Box
                                sx={{
                                    opacity: 0.4,
                                    cursor: 'not-allowed',
                                    px: 2.5,
                                    py: 1.25,
                                    borderRadius: '12px',
                                    bgcolor: CHIP_BG,
                                    border: '1px dashed rgba(255,255,255,0.2)',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 1,
                                }}>
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: 15,
                                        fontWeight: 700,
                                        color: '#fff',
                                    }}>
                                    {platform.label}
                                </Typography>
                                <LockOutlinedIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.5)' }} />
                            </Box>
                        </Tooltip>
                    );
                }

                return (
                    <Box
                        key={platform.id}
                        component="button"
                        type="button"
                        disabled={disabled}
                        onClick={() => {
                            if (!disabled) onToggle(platform.id);
                        }}
                        sx={{
                            ...chipTransition,
                            border: '1px solid',
                            borderColor: selected ? BRAND_LUXE_GOLD : 'rgba(255,255,255,0.1)',
                            borderRadius: '12px',
                            px: 2.5,
                            py: 1.25,
                            bgcolor: selected ? BRAND_LUXE_GOLD : CHIP_BG,
                            color: selected ? OBSIDIAN : '#fff',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 15,
                            fontWeight: selected ? 800 : 700,
                            cursor: disabled ? 'not-allowed' : 'pointer',
                            boxShadow: selected ? GOLD_GLOW_SM : 'none',
                            '&:hover': {
                                borderColor: selected ? BRAND_LUXE_GOLD : 'rgba(255, 193, 7, 0.3)',
                                bgcolor: selected ? BRAND_LUXE_GOLD : CHIP_BG,
                            },
                            '&:focus-visible': {
                                outline: `2px solid ${BRAND_LUXE_GOLD}`,
                                outlineOffset: 2,
                            },
                        }}>
                        {platform.label}
                    </Box>
                );
            })}
        </Box>
    );
}

export function UgcInfluencerTierCards({
    tiers,
    selectedTierId,
    onSelect,
    disabled = false,
}: {
    tiers: IInfluencerTierConfig[] | { id: string; name: string; reach: string }[];
    selectedTierId: string;
    onSelect: (tierId: string) => void;
    disabled?: boolean;
}) {
    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: {
                    xs: 'repeat(2, minmax(0, 1fr))',
                    sm: 'repeat(3, minmax(0, 1fr))',
                    lg: 'repeat(2, minmax(0, 1fr))',
                },
                gap: { xs: 1.5, sm: 2, lg: 3 },
                opacity: disabled ? 0.55 : 1,
            }}>
            {tiers.map(tier => {
                const active = selectedTierId === tier.id;
                const payout =
                    'min_payout_per_creator_usd' in tier && typeof tier.min_payout_per_creator_usd === 'number'
                        ? tier.min_payout_per_creator_usd
                        : null;
                return (
                    <Box
                        key={tier.id}
                        component="button"
                        type="button"
                        disabled={disabled}
                        onClick={() => {
                            if (!disabled) onSelect(tier.id);
                        }}
                        sx={{
                            ...chipTransition,
                            textAlign: 'left',
                            minHeight: { xs: 100, sm: 140, lg: 160 },
                            p: { xs: 2, sm: 2.5, lg: 4 },
                            borderRadius: '24px',
                            bgcolor: CARD_BG,
                            border: active ? `3px solid ${BRAND_LUXE_GOLD}` : '2px solid rgba(255,255,255,0.06)',
                            boxShadow: active ? '0 0 20px rgba(255, 193, 7, 0.15)' : 'none',
                            cursor: disabled ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            '&:hover': {
                                borderColor: active ? BRAND_LUXE_GOLD : 'rgba(255,255,255,0.2)',
                            },
                            '&:focus-visible': {
                                outline: `2px solid ${BRAND_LUXE_GOLD}`,
                                outlineOffset: 2,
                            },
                        }}>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: { xs: 15, sm: 18, lg: 24 },
                                fontWeight: 800,
                                color: active ? BRAND_LUXE_GOLD : '#fff',
                                lineHeight: 1.2,
                            }}>
                            {tier.name}
                        </Typography>
                        <Box>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: { xs: 11, sm: 13, lg: 15 },
                                    fontWeight: 600,
                                    color: active ? 'rgba(255,255,255,0.65)' : 'rgba(255,255,255,0.4)',
                                    mt: 1,
                                }}>
                                {tier.reach}
                            </Typography>
                            {payout != null ? (
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        fontSize: { xs: 10, sm: 12 },
                                        color: active ? 'rgba(255,201,101,0.85)' : 'rgba(255,255,255,0.35)',
                                        mt: 0.5,
                                    }}>
                                    Min ${payout.toLocaleString()}/creator
                                </Typography>
                            ) : null}
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
}

/** Wrapper for reward / platforms / tiers block — open layout like the HTML mock. */
export function UgcTargetingConfigPanel({ children }: { children: React.ReactNode }) {
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: { xs: 4, sm: 6 },
                py: { xs: 2, sm: 3 },
                px: { xs: 0.5, sm: 1 },
            }}>
            {children}
        </Box>
    );
}

export function UgcTargetingSection({ children }: { children: React.ReactNode }) {
    return <Box component="section">{children}</Box>;
}

/** Compact card shell for budget / dates (unchanged hierarchy). */
export function UgcFormFieldCard({ children }: { children: React.ReactNode }) {
    return (
        <Box
            sx={{
                bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                border: `1px solid ${BRAND_SURFACE_CONTAINER}`,
                p: { xs: 2, sm: 3 },
                borderRadius: 3,
            }}>
            {children}
        </Box>
    );
}
